import { SCREENS } from '../../lib/agent/discovery';
import { ROLE_OF_SEAT } from '../../config/navigation';
import {
  resolveScreen, pathFor, screenForPath, paramsFor, seatAllows,
} from '../../lib/agent/screens';
import { formatApiResult } from '../../lib/agent/apiResult';
import { agentSingleton } from '../../lib/agent/singleton';
import { agentRuntime } from './runtime';
import { debug } from '../../lib/debug';

/**
 * Agent-driven navigation: screen name + params in, a URL the user can see out.
 *
 * The router lives in React and the agent does not. `AgentNavigationBridge`
 * copies `useNavigate()` and `useLocation()` into `navigationRefs` after every
 * commit, so this module can navigate and then watch the location without ever
 * holding `navigate` in an effect's dependency list — in react-router v6 its
 * identity changes on every location change, and an effect keyed on it would
 * re-run (and, in the provider, reconnect) on every page the user opens.
 *
 * Only failures are reported. Every `[api_result]` becomes a new spoken turn on
 * the server, and the next question already carries the screen the user landed
 * on, so a success report would only make the agent talk twice.
 */

/**
 * Written by `AgentNavigationBridge`; null until the bridge has committed.
 * `commits` are told of every location the router commits, so a wait cannot
 * miss one that a page replaced before the next poll.
 *
 * Registered rather than declared, so the bridge and this module share one
 * object even when the dev server has evaluated this file twice — see
 * `lib/agent/singleton.js` for what that costs when they drift apart.
 */
export const navigationRefs = agentSingleton(
  'navigationRefs',
  () => ({ navigate: null, location: null, commits: new Set() }),
);

/** How long a navigation may take to show up in the router before it counts as failed. */
const SETTLE_TIMEOUT_MS = 2000;
/** Poll step while waiting. Timers rather than animation frames: frames stop in a background tab. */
const SETTLE_POLL_MS = 25;
/** How long "the agent brought the user here" stays true for the page that mounts. */
const ARRIVAL_TTL_MS = 20000;

/* ── arrival marker ─────────────────────────────────────────────────────── */

/** Screen key → when the agent last navigated to it. Module memory only, never persisted. */
const arrivals = new Map();

/** Accepts a screen key or an agent-facing screen name, so callers cannot disagree on spelling. */
const arrivalKey = (keyOrName) => {
  if (keyOrName == null || keyOrName === '') return null;
  return resolveScreen(keyOrName) || String(keyOrName);
};

/** Remember that the agent is about to open this screen. */
export function noteAgentNavigation(key) {
  const k = arrivalKey(key);
  if (k) arrivals.set(k, Date.now());
}

/**
 * True exactly once if the agent navigated to `key` within the last 20 s.
 *
 * Consuming is what keeps a page from reporting on a visit the user made by
 * hand a minute later: the first mount after the agent's navigation takes the
 * marker, and every later mount finds nothing.
 */
export function consumeAgentNavigation(key) {
  const k = arrivalKey(key);
  if (!k || !arrivals.has(k)) return false;
  const at = arrivals.get(k);
  arrivals.delete(k);
  return Date.now() - at <= ARRIVAL_TTL_MS;
}

/** The same question without taking the marker. Expired markers are dropped on the way. */
export function wasAgentNavigation(key) {
  const k = arrivalKey(key);
  if (!k || !arrivals.has(k)) return false;
  if (Date.now() - arrivals.get(k) > ARRIVAL_TTL_MS) {
    arrivals.delete(k);
    return false;
  }
  return true;
}

/* ── helpers ────────────────────────────────────────────────────────────── */

const stripQ = (search) => String(search || '').replace(/^\?/, '');

/**
 * Two query strings mean the same thing when they hold the same pairs.
 *
 * Compared as sorted pairs rather than as text: the browser keeps whatever
 * encoding the URL was typed with (`funds=a,b`) while URLSearchParams writes
 * `funds=a%2Cb`, and treating those as different would push an identical
 * history entry on every stay-put navigate.
 */
function sameSearch(a, b) {
  const pairs = (s) => [...new URLSearchParams(stripQ(s)).entries()]
    .map(([k, v]) => `${k}\u0000${v}`)
    .sort();
  const x = pairs(a);
  const y = pairs(b);
  return x.length === y.length && x.every((p, i) => p === y[i]);
}

/** Where the router says the user is, falling back to the provider's view before the bridge commits. */
export function currentAgentLocation() {
  const loc = navigationRefs.location;
  if (loc && typeof loc.pathname === 'string') {
    return { pathname: loc.pathname, search: loc.search || '' };
  }
  return agentRuntime().currentLocation();
}

/** The seat the check runs against: the account's own, else the one being previewed. */
function currentSeat() {
  const ui = agentRuntime().store.getState()?.ui;
  return ui?.assignedRole || ui?.role || null;
}

const screenLabel = (key) => SCREENS[key]?.screen || key;

/**
 * Resolve true once the router shows `pathname` (and `search`, when given),
 * false at the deadline.
 *
 * Each commit is checked as it happens as well as polled: a page that rewrites
 * its URL straight after landing (or a reader's click) can replace the location
 * within one poll, and the navigate did land even though no poll saw it.
 */
function waitForLocation(pathname, search, timeoutMs) {
  return new Promise((resolve) => {
    const deadline = Date.now() + timeoutMs;
    const matches = (loc) => {
      if (!loc || loc.pathname !== pathname) return false;
      return !search || sameSearch(loc.search, search);
    };
    if (matches(navigationRefs.location)) {
      resolve(true);
      return;
    }
    let timer = null;
    const finish = (landed) => {
      clearInterval(timer);
      navigationRefs.commits.delete(onCommit);
      resolve(landed);
    };
    function onCommit(loc) {
      if (matches(loc)) finish(true);
    }
    navigationRefs.commits.add(onCommit);
    timer = setInterval(() => {
      if (matches(navigationRefs.location)) finish(true);
      else if (Date.now() >= deadline) finish(false);
    }, SETTLE_POLL_MS);
  });
}

/* ── navigate ───────────────────────────────────────────────────────────── */

/**
 * Open a catalogue screen with params, the way the agent asks for it.
 *
 * Steps, in order: resolve the name → build the URL → check the seat → merge
 * with the current query on the same screen (see below) → skip an identical
 * navigate → mark the arrival → navigate → wait (≤ 2 s) for the router to show
 * it. Tabs and controls are query params, so "switch to the risk tab" is this
 * same call with `{ tab: 'risk' }`.
 *
 * Three rules keep a stay-put navigate harmless, which matters because agents
 * trained on the app echo the current screen on nearly every turn:
 * - Navigating to the screen already open reuses its route params when the
 *   agent omits them, so `{ screen: 'FundDeepDive', params: { tab: 'risk' } }`
 *   works without restating the fund.
 * - On the same pathname the given query params override and every other one
 *   is kept, and a navigate that changes nothing does nothing.
 * - On the same screen with another subject (a new route param: "the same view
 *   for fund B") the view carries over — the tab and the other enumerated
 *   controls — as the page's own subject pickers carry it. Free-form params
 *   (a goal, a security group) belonged to the old subject and are dropped.
 *
 * The seat is checked here, before navigating, rather than left to the shell's
 * guard: the guard's bounce to the landing page is exactly the silent
 * substitution the agent must be told about.
 *
 * On failure, unless `silent`, one `[api_result] navigate failed: <reason>` is
 * sent. Success is never reported.
 */
export async function agentNavigate({
  screen, params = {}, replace = false, silent = false, clear = [],
} = {}) {
  const fail = (code, reason) => {
    debug('agent → navigate failed', { screen, params, code, reason });
    if (!silent) {
      agentRuntime().report(formatApiResult({ op: 'navigate', ok: false, error: reason }));
    }
    return { ok: false, code, reason };
  };

  try {
    const key = resolveScreen(screen);
    if (!key) return fail('unknown_screen', `unknown screen ${JSON.stringify(String(screen ?? ''))}`);

    const here = currentAgentLocation();
    const hereKey = here.pathname ? screenForPath(here.pathname) : null;
    const given = params && typeof params === 'object' && !Array.isArray(params) ? params : {};

    /* Route params only — the query is merged below, by the same-screen rules,
       and copying it here would turn "keep the other params" into "resend them". */
    let effective = given;
    if (hereKey === key) {
      const routeNames = SCREENS[key]?.routeParams || [];
      const current = paramsFor(key, here.pathname, here.search) || {};
      const inherited = {};
      routeNames.forEach((name) => {
        if (given[name] == null && current[name] != null) inherited[name] = current[name];
      });
      effective = { ...inherited, ...given };
    }

    const built = pathFor(key, effective);
    if (!built || !built.ok) {
      return fail(built?.code || 'missing_param', built?.reason || `could not build a URL for ${screenLabel(key)}`);
    }

    const seat = currentSeat();
    if (!seat) return fail('no_router', 'the app has not finished signing in');
    if (!seatAllows(seat, key)) {
      return fail('not_allowed', `${screenLabel(key)} is not available to the ${ROLE_OF_SEAT[seat] || seat} role`);
    }

    const navigate = navigationRefs.navigate;
    const location = navigationRefs.location;
    if (typeof navigate !== 'function' || !location) {
      return fail('no_router', 'the app router is not ready');
    }

    let search = stripQ(built.search);
    const samePath = built.pathname === location.pathname;
    if (samePath || screenForPath(location.pathname) === key) {
      const merged = new URLSearchParams(stripQ(location.search));
      if (!samePath) {
        const view = new Set(Object.keys(SCREENS[key]?.values || {}));
        [...new Set(merged.keys())].forEach((name) => {
          if (!view.has(name)) merged.delete(name);
        });
      }
      new URLSearchParams(search).forEach((value, name) => merged.set(name, value));
      /* Query params the caller says no longer describe the view, removed
         rather than kept by the same-screen rule. */
      (Array.isArray(clear) ? clear : []).forEach((name) => merged.delete(name));
      /* Commas unescaped, as `useUrlParam` writes them, so a list param keeps
         reading `funds=a,b` in the address bar after the agent touches it. */
      search = merged.toString().replace(/%2C/gi, ',');
      if (samePath && sameSearch(location.search, search)) {
        debug('agent → navigate (already there)', { key, pathname: built.pathname, search });
        return { ok: true, key, pathname: built.pathname, search: stripQ(location.search) };
      }
    }

    /* Marked on the screen that will actually mount: a forwarding screen
       (`clients`) builds the URL of the one it lands on. */
    noteAgentNavigation(screenForPath(built.pathname) || key);
    debug('agent → navigate', { key, pathname: built.pathname, search, replace });
    navigate({ pathname: built.pathname, search: search ? `?${search}` : '' }, { replace: Boolean(replace) });

    const landed = await waitForLocation(built.pathname, search, SETTLE_TIMEOUT_MS);
    if (!landed) {
      return fail('timeout', `${screenLabel(key)} did not open`);
    }
    return { ok: true, key, pathname: built.pathname, search };
  } catch (err) {
    /* A catalogue or router exception is a client bug, but the agent still gets
       exactly one answer for the navigate it asked for. */
    return fail('no_router', `client error — ${err?.message || String(err)}`);
  }
}
